import apiClient from "@/services/api/apiClient"
import type {
  NotificationCatalog,
  NotificationListFilters,
  NotificationPreference,
  NotificationsPage,
  UnreadCountResponse,
} from "../types"

export const notificationQueryKeys = {
  all: ["notifications"] as const,
  unreadCount: ["notifications", "unread-count"] as const,
  /** Base key — every filtered list variant nests under it. */
  lists: ["notifications", "list"] as const,
  list: (filters: NotificationListFilters = {}, pageSize?: number) =>
    [
      "notifications",
      "list",
      {
        unreadOnly: filters.unreadOnly ?? false,
        needsAction: filters.needsAction ?? false,
        pageSize: pageSize ?? null,
      },
    ] as const,
  types: ["notifications", "types"] as const,
  preferences: ["notifications", "preferences"] as const,
}

export const notificationApiCalls = {
  getNotifications: async (
    limit: number,
    cursor?: string,
    filters: NotificationListFilters = {},
  ): Promise<NotificationsPage> => {
    const response = await apiClient.get<NotificationsPage>("/notifications", {
      params: {
        limit,
        cursor: cursor ?? undefined,
        unread_only: filters.unreadOnly ? true : undefined,
        needs_action: filters.needsAction ? true : undefined,
      },
    })
    return response.data
  },
  getUnreadCount: async (): Promise<UnreadCountResponse> => {
    const response = await apiClient.get<UnreadCountResponse>(
      "/notifications/unread-count",
    )
    return response.data
  },
  markRead: async (notificationId: string): Promise<void> => {
    await apiClient.post(`/notifications/${notificationId}/read`)
  },
  markActed: async (notificationId: string): Promise<void> => {
    await apiClient.post(`/notifications/${notificationId}/acted`)
  },
  markAllRead: async (): Promise<void> => {
    await apiClient.post("/notifications/read-all")
  },
  getCatalog: async (): Promise<NotificationCatalog> => {
    const response = await apiClient.get<NotificationCatalog>(
      "/notifications/types",
    )
    return response.data
  },
  getPreferences: async (): Promise<NotificationPreference[]> => {
    const response = await apiClient.get<{
      preferences: NotificationPreference[]
    }>("/notifications/preferences")
    return response.data.preferences
  },
  updatePreference: async (
    preference: NotificationPreference,
  ): Promise<void> => {
    await apiClient.put("/notifications/preferences", preference)
  },
}
